import { supabase } from "@/integrations/supabase/client";

export type Post = {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string;
  cover_image: string | null;
  category: string | null;
  status: "draft" | "published";
  author_id: string | null;
  published_at: string | null;
  meta_title: string | null;
  meta_description: string | null;
  keywords: string[] | null;
  created_at: string;
  updated_at: string;
};

export const isScheduled = (p: Pick<Post, "status" | "published_at">) =>
  p.status === "published" && !!p.published_at && new Date(p.published_at) > new Date();

export const formatSchedule = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).replace(",", " às");

const STOPWORDS = new Set("a o as os um uma uns umas de da do das dos em na no nas nos para pra por pelo pela pelos pelas com sem e ou que como seu sua seus suas se ao aos à às mais muito sobre entre até é ser the and of to for in".split(" "));

/** Short, clean slug: no accents, no stopwords, up to 5 words. */
export const generateSlug = (title: string) => {
  const words = title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, " ")
    .split(/[\s-]+/)
    .filter(Boolean);
  const kept = words.filter((w) => !STOPWORDS.has(w));
  return (kept.length ? kept : words).slice(0, 5).join("-").slice(0, 60);
};

export const readingTime = (html: string) => {
  const words = html.replace(/<!--[\s\S]*?-->/g, "").replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

export const getCoverUrl = async (path: string | null): Promise<string | null> => {
  if (!path) return null;
  if (/^https?:\/\//.test(path)) return path;
  const { data } = await supabase.storage
    .from("blog-covers")
    .createSignedUrl(path, 60 * 60);
  return data?.signedUrl ?? null;
};

export const formatPostDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      })
    : "";

export const SANITIZE_ATTRS = ["style", "data-type", "data-cols", "data-variant", "data-bg", "data-pad", "data-align", "data-size", "data-leadform", "target"];
